import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LocalAtmIcon from '@mui/icons-material/LocalAtm'
import ListAltIcon from '@mui/icons-material/ListAlt'
import FlutterDashIcon from '@mui/icons-material/FlutterDash'
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation'
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt'
import { useAuth } from "./api/authStore"
import { loginUrl } from './config/config'
import PaymentDialog from './PaymentDialog'
import ThemeToggle from './components/ThemeToggle'
import logo from './img/logo.svg'
import landingLogo from './img/landing-logo.png'

// Компонент навигационного элемента
const NavItem = ({ icon: Icon, label, href, isActive = false, onClick, customBgColor, customHoverBgColor }) => {
    const activeBgColor = customBgColor || 'var(--primary-orange)'
    const hoverBgColor = customHoverBgColor || 'var(--dark-gray)'
    
    // Если есть onClick (для кнопок типа Help, Logout), используем обычную кнопку
    if (onClick) {
        return (
            <button 
                onClick={onClick}
                className="nav-button"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-md) var(--spacing-lg)',
                    margin: 'var(--spacing-xs) var(--spacing-md)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-muted)',
                    textDecoration: 'none',
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                    backgroundColor: 'transparent',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                    e.target.style.backgroundColor = hoverBgColor
                }}
                onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent'
                }}
            >
                <Icon style={{ 
                    fontSize: '20px', 
                    marginRight: 'var(--spacing-md)',
                    color: 'var(--primary-orange)'
                }} />
                <span style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: '400'
                }}>
                    {label}
                </span>
            </button>
        )
    }
    
    // Для навигационных ссылок используем Link
    return (
        <Link 
            to={href} 
            className="nav-link"
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: 'var(--spacing-md) var(--spacing-lg)',
                margin: 'var(--spacing-xs) var(--spacing-md)',
                borderRadius: 'var(--radius-md)',
                color: isActive ? 'var(--primary-orange)' : 'var(--text-muted)',
                textDecoration: 'none',
                transition: 'border-color 0.15s ease, color 0.15s ease',
                backgroundColor: 'transparent',
                border: isActive ? '1px solid var(--primary-orange)' : '1px solid transparent',
                position: 'relative',
                overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
                if (!isActive) {
                    e.target.style.backgroundColor = hoverBgColor
                }
            }}
            onMouseLeave={(e) => {
                if (!isActive) {
                    e.target.style.backgroundColor = 'transparent'
                }
            }}
        >
            <Icon style={{ 
                fontSize: '20px', 
                marginRight: 'var(--spacing-md)',
                color: isActive ? 'var(--primary-orange)' : 'var(--primary-orange)'
            }} />
            <span style={{
                fontSize: 'var(--text-sm)',
                fontWeight: isActive ? '600' : '400'
            }}>
                {label}
            </span>
        </Link>
    )
}

// Компонент аватара с пульсирующим лого
const UserAvatar = ({ email }) => {
    return (
        <div style={{
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 'var(--spacing-md)',
            position: 'relative'
        }}>
            <img 
                src={landingLogo} 
                alt="XSocks Logo" 
                style={{
                    width: '40px',
                    height: '40px',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 4px 8px rgba(255, 107, 53, 0.3))',
                    animation: 'pulse 2s ease-in-out infinite'
                }} 
            />
            <style>{`
                @keyframes pulse {
                    0%, 100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    50% {
                        transform: scale(1.1);
                        opacity: 0.8;
                    }
                }
            `}</style>
        </div>
    )
}

// Компонент модального окна с контактными данными
const HelpModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
        }} onClick={onClose}>
            <div style={{
                background: 'var(--medium-gray)',
                border: '1px solid var(--light-gray)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-2xl)',
                maxWidth: '500px',
                width: '90%',
                boxShadow: 'var(--shadow-card)',
                position: 'relative'
            }} onClick={(e) => e.stopPropagation()}>
                {/* Кнопка закрытия */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: 'var(--spacing-md)',
                        right: 'var(--spacing-md)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-muted)',
                        fontSize: 'var(--text-xl)',
                        cursor: 'pointer',
                        padding: '4px'
                    }}
                >
                    ✕
                </button>

                {/* Заголовок */}
                <h2 style={{
                    color: 'var(--text-light)',
                    fontSize: 'var(--text-2xl)',
                    fontWeight: '700',
                    marginBottom: 'var(--spacing-lg)',
                    textAlign: 'center',
                    background: 'var(--gradient-orange)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                }}>
                    Technical Support
                </h2>

                {/* Контактная информация */}
                <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                    <div style={{
                        background: 'var(--dark-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--spacing-lg)',
                        marginBottom: 'var(--spacing-md)'
                    }}>
                        <h3 style={{
                            color: 'var(--primary-orange)',
                            fontSize: 'var(--text-lg)',
                            marginBottom: 'var(--spacing-md)',
                            fontWeight: '600'
                        }}>
                            📧 Email Support
                        </h3>
                        <p style={{
                            color: 'var(--text-light)',
                            fontSize: 'var(--text-base)',
                            marginBottom: 'var(--spacing-sm)'
                        }}>
                            <strong>xsocks@yahoo.com</strong>
                        </p>
                        <p style={{
                            color: 'var(--text-muted)',
                            fontSize: 'var(--text-sm)',
                            lineHeight: '1.5'
                        }}>
                            Send us an email with your question or issue. We typically respond within 24 hours.
                        </p>
                    </div>

                    <div style={{
                        background: 'var(--dark-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--spacing-lg)'
                    }}>
                        <h3 style={{
                            color: 'var(--primary-orange)',
                            fontSize: 'var(--text-lg)',
                            marginBottom: 'var(--spacing-md)',
                            fontWeight: '600'
                        }}>
                            💬 Telegram Support
                        </h3>
                        <p style={{
                            color: 'var(--text-light)',
                            fontSize: 'var(--text-base)',
                            marginBottom: 'var(--spacing-sm)'
                        }}>
                            <a 
                                href="https://t.me/xsocks_support" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{
                                    color: 'var(--primary-orange)',
                                    textDecoration: 'none',
                                    fontWeight: '600'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.textDecoration = 'underline'
                                    e.target.style.color = 'var(--light-orange)'
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.textDecoration = 'none'
                                    e.target.style.color = 'var(--primary-orange)'
                                }}
                            >
                                @xsocks_support
                            </a>
                        </p>
                        <p style={{
                            color: 'var(--text-muted)',
                            fontSize: 'var(--text-sm)',
                            lineHeight: '1.5'
                        }}>
                            Get instant help through our Telegram support channel. Available for quick assistance.
                        </p>
                    </div>
                </div>

                {/* Кнопка закрытия */}
                <div style={{ textAlign: 'center' }}>
                    <button
                        onClick={onClose}
                        className="btn btn-primary"
                        style={{ minWidth: '120px' }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}

const LeftSideMenu = React.memo((props) => {
    const { getAccessTokenSilently, setToken, logout } = useAuth()
    const [openPaymentDialog, setOpentPaymentDialog] = React.useState(false)
    const [showHelpModal, setShowHelpModal] = React.useState(false)
    const location = useLocation()
    
    // Используем props.user вместо useAuth().user для консистентности
    const user = props.user
    const accountEmail = user?.email || ''
    const balance = user?.balanceCents ? (user.balanceCents/100).toFixed(2) : '0.00'
    const activeProxiesList = props.active?.leases || []
    const activeProxiesCount = activeProxiesList.length

    // Логирование только в режиме разработки
    React.useEffect(() => {
        if (user && process.env.NODE_ENV === 'development') {
        }
    }, [user])

    const handleLogout = () => {
        logout(loginUrl)
    }

    const handleHelpClick = (e) => {
        e.preventDefault()
        setShowHelpModal(true)
    }

    const handleOpenPaymentDialog = () => {
        setOpentPaymentDialog(true)
    }

    // Определяем активную страницу
    const currentPath = location.pathname

    return (
        <div className="left-side-menu" style={{
            width: '280px',
            height: '100vh',
            background: 'var(--black)',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: 1000
        }}>
            {/* Заголовок */}
            <div style={{
                padding: 'var(--spacing-xl) var(--spacing-lg)',
                borderBottom: '1px solid var(--light-gray)',
                background: 'var(--gradient-dark)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <h1 style={{
                        fontSize: 'var(--text-xl)',
                        fontWeight: '700',
                        background: 'var(--gradient-orange)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        margin: 0,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em'
                    }}>
                        {process.env.REACT_APP_NAME}
                    </h1>
                </div>
            </div>

            {/* Профиль пользователя */}
            <div style={{
                padding: 'var(--spacing-lg)',
                borderBottom: '1px solid var(--light-gray)',
                background: 'var(--medium-gray)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 'var(--spacing-md)'
                }}>
                    <UserAvatar email={accountEmail} />
                    <div style={{ flex: 1 }}>
                        <div style={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: '600',
                            color: 'var(--text-light)',
                            marginBottom: 'var(--spacing-xs)'
                        }}>
                            <Link 
                                to='/account' 
                                style={{
                                    color: 'var(--text-light)',
                                    textDecoration: 'none'
                                }}
                            >
                                {accountEmail.replace(/(.{20})..+/, "$1…")}
                            </Link>
                        </div>
                        <div style={{
                            fontSize: 'var(--text-xs)',
                            color: 'var(--text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-sm)'
                        }}>
                            <span>Balance: <strong style={{ color: 'var(--primary-orange)' }}>${balance}</strong></span>
                            <button
                                onClick={handleOpenPaymentDialog}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#00B24A',
                                    fontSize: 'var(--text-xs)',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    padding: 0
                                }}
                            >
                                Add Funds
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Навигационное меню */}
            <div style={{ flex: 1, padding: 'var(--spacing-md) 0' }}>
                <NavItem 
                    icon={LocalGasStationIcon} 
                    label="Proxies Store" 
                    href="/proxies"
                    isActive={currentPath === '/proxies'}
                />
                
                <NavItem 
                    icon={FlutterDashIcon} 
                    label={`Active Proxies${activeProxiesCount > 0 ? ` (${activeProxiesCount})` : ''}`} 
                    href="/active"
                    isActive={currentPath === '/active'}
                />
                
                <NavItem 
                    icon={AccessTimeIcon} 
                    label="History" 
                    href="/history"
                    isActive={currentPath === '/history'}
                />
                
                <NavItem 
                    icon={LocalAtmIcon} 
                    label="Transactions" 
                    href="/transactions"
                    isActive={currentPath === '/transactions'}
                />
                
                <NavItem 
                    icon={ListAltIcon} 
                    label="Tickets" 
                    href="/tickets"
                    isActive={currentPath === '/tickets'}
                />
            </div>

            {/* Нижняя секция */}
            <div style={{
                padding: 'var(--spacing-md)',
                borderTop: '1px solid var(--light-gray)',
                background: 'var(--black)'
            }}>
                <ThemeToggle />
                
                <NavItem 
                    icon={PsychologyAltIcon} 
                    label="Help" 
                    onClick={handleHelpClick}
                />
                
                <NavItem 
                    icon={PowerSettingsNewIcon} 
                    label="Logout" 
                    onClick={handleLogout}
                />
            </div>

            <PaymentDialog 
                open={openPaymentDialog} 
                setOpentPaymentDialog={setOpentPaymentDialog} 
                apiClient={props.apiClient}
            />
            
            <HelpModal 
                isOpen={showHelpModal} 
                onClose={() => setShowHelpModal(false)}
            />
        </div> 
    )
})

export default LeftSideMenu